import React, { useState } from 'react';
import {
  Alert,
  Button,
  Drawer,
  Modal,
  Space,
  Table,
  Tabs,
  Upload,
  message,
  Spin,
} from 'antd';
import { InboxOutlined } from '@ant-design/icons';

import cdslRealInventoryReconcileService from '../../../services/cdslRealInventoryReconcileService';
import {
  buildBulkAddPayload,
  getAlreadyAddedColumns,
  getAmbiguousColumns,
  getReadyMatchColumns,
  getStatementFormatLabel,
  getUnmatchedExtractionColumns,
  rowKeyForMatch,
  selectedRowsMissingPrice,
} from './cdslRealInventoryMatch.helpers';

const { Dragger } = Upload;

function CdslRealInventoryReconcileDrawer({ open, onClose, onSuccess }) {
  const [step, setStep] = useState('upload');
  const [extracting, setExtracting] = useState(false);
  const [matching, setMatching] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [reconcileData, setReconcileData] = useState(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [priceByRowKey, setPriceByRowKey] = useState({});
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);

  const resetState = () => {
    setStep('upload');
    setExtracting(false);
    setMatching(false);
    setConfirming(false);
    setReconcileData(null);
    setSelectedRowKeys([]);
    setPriceByRowKey({});
    setConfirmOpen(false);
    setBulkResult(null);
  };

  const handleClose = () => {
    resetState();
    onClose?.();
  };

  const handlePriceChange = (key, value) => {
    setPriceByRowKey((prev) => ({ ...prev, [key]: value }));
  };

  const runMatch = async (extractions) => {
    setMatching(true);
    try {
      const data = await cdslRealInventoryReconcileService.matchCdslCredits(extractions);
      setReconcileData(data);
      const readyKeys = (data.matched || [])
        .filter((row) => row.match_state === 'ready')
        .map(rowKeyForMatch);
      setSelectedRowKeys(readyKeys);
      setStep('review');
    } catch (err) {
      message.error(err?.response?.data?.message || err?.message || 'Reconciliation failed');
    } finally {
      setMatching(false);
    }
  };

  const handleUpload = async (file) => {
    setExtracting(true);
    try {
      const ocrResponse = await cdslRealInventoryReconcileService.extractCdslStatement(file);
      await runMatch(ocrResponse);
    } catch (err) {
      const raw = err?.response?.data;
      const errMsg =
        (typeof raw === 'object' && (raw?.message || raw?.error)) ||
        (typeof raw === 'string' ? raw : null) ||
        err?.message ||
        'OCR extraction failed';
      message.error(errMsg);
    } finally {
      setExtracting(false);
    }
    return false;
  };

  const readyRows = (reconcileData?.matched || []).filter((r) => r.match_state === 'ready');
  const alreadyAddedRows = reconcileData?.already_added || [];
  const unmatchedExtractions = reconcileData?.unmatched_extractions || [];
  const ambiguousRows = reconcileData?.ambiguous || [];

  const selectedReadyRows = readyRows.filter((row) =>
    selectedRowKeys.includes(rowKeyForMatch(row))
  );

  const hasMissingPrice = selectedRowsMissingPrice(selectedReadyRows, priceByRowKey);

  const runBulkAdd = async () => {
    if (selectedReadyRows.length === 0) {
      message.warning('Select at least one ready row to add.');
      return;
    }
    if (hasMissingPrice) {
      message.warning('Enter supplier price for all selected rows.');
      return;
    }

    setConfirming(true);
    try {
      const lots = buildBulkAddPayload(selectedReadyRows, priceByRowKey);
      const result = await cdslRealInventoryReconcileService.bulkAddRealLots(lots);
      setBulkResult(result);

      if (result.summary?.failed === 0) {
        message.success(`Added ${result.summary.succeeded} real lot(s).`);
        setConfirmOpen(false);
        onSuccess?.();
        handleClose();
      } else if (result.summary?.succeeded > 0) {
        message.warning(`${result.summary.succeeded} succeeded, ${result.summary.failed} failed.`);
        setConfirmOpen(false);
        onSuccess?.();
      } else {
        message.error('Bulk add failed for all selected rows.');
      }
    } catch (err) {
      message.error(err?.response?.data?.message || err?.message || 'Bulk add failed');
    } finally {
      setConfirming(false);
    }
  };

  const statementFormat = reconcileData?.statement_format || 'cdsl';

  const tabItems = [
    {
      key: 'ready',
      label: `Ready (${readyRows.length})`,
      children: (
        <Table
          rowKey={rowKeyForMatch}
          size="small"
          dataSource={readyRows}
          columns={getReadyMatchColumns(priceByRowKey, handlePriceChange, statementFormat)}
          pagination={false}
          scroll={{ x: 'max-content' }}
          rowSelection={{
            selectedRowKeys,
            onChange: setSelectedRowKeys,
          }}
          locale={{ emptyText: 'No ready matches' }}
        />
      ),
    },
    {
      key: 'already_added',
      label: `Already added (${alreadyAddedRows.length})`,
      children: (
        <Table
          rowKey={(row) => `added-${row.extraction?.row_index}-${row.fingerprint}`}
          size="small"
          dataSource={alreadyAddedRows}
          columns={getAlreadyAddedColumns()}
          pagination={false}
          locale={{ emptyText: 'No duplicates' }}
        />
      ),
    },
    {
      key: 'unmatched_pdf',
      label: `Unmatched PDF (${unmatchedExtractions.length})`,
      children: (
        <Table
          rowKey={(row) => `ext-${row.extraction?.row_index}`}
          size="small"
          dataSource={unmatchedExtractions}
          columns={getUnmatchedExtractionColumns(statementFormat)}
          pagination={false}
          locale={{ emptyText: 'All PDF rows matched' }}
        />
      ),
    },
  ];

  if (ambiguousRows.length > 0) {
    tabItems.splice(2, 0, {
      key: 'ambiguous',
      label: `Ambiguous (${ambiguousRows.length})`,
      children: (
        <>
          <Alert
            type="warning"
            showIcon
            className="mb-3"
            message="Duplicate or conflicting matches. Add these manually if needed."
          />
          <Table
            rowKey={(row) => `amb-${row.extraction?.row_index}-${row.product?.uuid || 'x'}-${row.supplier?.uuid || 'y'}`}
            size="small"
            dataSource={ambiguousRows}
            columns={getAmbiguousColumns()}
            pagination={false}
          />
        </>
      ),
    });
  }

  return (
    <>
      <Drawer
        title="Reconcile credit statement"
        open={open}
        onClose={handleClose}
        width={1024}
        destroyOnClose
        extra={
          step === 'review' ? (
            <Space>
              <Button
                onClick={() => {
                  setStep('upload');
                  setReconcileData(null);
                  setSelectedRowKeys([]);
                  setPriceByRowKey({});
                }}
              >
                Upload another PDF
              </Button>
              <Button
                type="primary"
                disabled={selectedReadyRows.length === 0 || hasMissingPrice}
                onClick={() => setConfirmOpen(true)}
              >
                Add {selectedReadyRows.length} real lot(s)
              </Button>
            </Space>
          ) : null
        }
      >
        {step === 'upload' ? (
          <Spin
            spinning={extracting || matching}
            tip={extracting ? 'Extracting PDF…' : 'Matching products and suppliers…'}
          >
            <p className="text-sm text-gray-600 mb-4">
              Upload a CDSL or NSDL credit statement. Format is auto-detected from the PDF.
              Credit lines are matched to products by ISIN and suppliers by demat (full number
              for CDSL, last 8 digits for NSDL). Manual Add Lot per product is unchanged.
            </p>
            <Dragger
              accept="application/pdf,.pdf,image/*"
              multiple={false}
              showUploadList={false}
              beforeUpload={handleUpload}
              disabled={extracting || matching}
            >
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">Click or drag credit statement here</p>
              <p className="ant-upload-hint">PDF or image. OCR may take up to 2 minutes.</p>
            </Dragger>
          </Spin>
        ) : (
          <div className="space-y-4">
            {reconcileData?.summary ? (
              <Alert
                type="info"
                showIcon
                message={`${getStatementFormatLabel(statementFormat)} statement · ${reconcileData.summary.extraction_count} PDF row(s) · ${reconcileData.summary.matched_ready} ready · ${reconcileData.summary.already_added_count} already added · ${reconcileData.summary.unmatched_extraction_count} unmatched · ${reconcileData.summary.ambiguous_count} ambiguous`}
              />
            ) : null}
            <Tabs items={tabItems} />
            {bulkResult?.results?.some((r) => !r.success) ? (
              <Alert
                type="error"
                showIcon
                message="Some lots failed to add"
                description={
                  <ul className="list-disc pl-4 mt-2 text-sm">
                    {bulkResult.results
                      .filter((r) => !r.success)
                      .map((r) => (
                        <li key={r.fingerprint}>
                          {r.fingerprint?.slice(0, 8)}…: {r.message}
                        </li>
                      ))}
                  </ul>
                }
              />
            ) : null}
          </div>
        )}
      </Drawer>

      <Modal
        title="Confirm bulk real inventory add"
        open={confirmOpen}
        onCancel={() => setConfirmOpen(false)}
        onOk={runBulkAdd}
        confirmLoading={confirming}
        okText={`Add ${selectedReadyRows.length} lot(s)`}
      >
        <p className="text-sm text-gray-600">
          This will create {selectedReadyRows.length} real inventory lot(s) across{' '}
          {new Set(selectedReadyRows.map((r) => r.product?.uuid)).size} product(s).
          Supplier is matched by demat ({statementFormat === 'nsdl' ? 'last 8 digits' : 'full number'});
          product is matched by ISIN from the statement.
        </p>
      </Modal>
    </>
  );
}

export default CdslRealInventoryReconcileDrawer;
